import '../app.ts'
import '/@front:css/views/properties.view.css'
import '@splidejs/vue-splide/css/core'

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { Splide, SplideSlide } from '@splidejs/vue-splide'
import type { MediaPlayerElement } from 'vidstack'
import { defineCustomElements } from 'vidstack/elements'
import { nextTick } from 'vue'

import BlurhashImage from '/@front:shared/components/blurhash/image.vue'
import { showUpgradeModal, useModal } from '/@front:shared/composables/modal'
import { mountPage } from '/@front:shared/mount-page'
import { http } from '/@front:shared/plugins/http'

defineCustomElements()

type ViewingVideo = {
  src: string
  poster: string | null
  isTeaser: boolean
}

const fixedHeight = Math.max(220, Math.min(400, Math.ceil(window.innerWidth / 2)))

mountPage({
  name: 'properties-view',
  components: {
    BlurhashImage,
    Splide,
    SplideSlide,
  },
  data() {
    return {
      splideOptions: {
        type: window.innerWidth < 800 ? 'loop' : 'slide',
        width: '100dvw',
        fixedHeight: `${fixedHeight}px`,
        gap: '10px',
        autoWidth: true,
        pagination: false,
      },
      videoSource: null as string | null,
      videoPoster: null as string | null,
      isTeaser: false,
      teaserEnded: false,
      isVideoFullscreen: false,
    }
  },
  methods: {
    openVideo(e: MouseEvent) {
      const link = (e.target as HTMLElement).closest('a') as HTMLAnchorElement
      const img = link.querySelector('img') as HTMLImageElement

      this.playVideo({
        src: link.href,
        poster: img ? img.src : null,
        isTeaser: false,
      })
    },

    async openViewingVideo(e: MouseEvent) {
      const link = (e.target as HTMLElement).closest('a') as HTMLAnchorElement

      // The http plugin reloads the page on a 403, which here means the teaser or premium has
      // gone since the page rendered.
      const { data, status } = await http.get<ViewingVideo>(link.href, {
        validateStatus: (s: number) => s === 200 || s === 403,
      })

      if (status === 403) {
        this.showUpgradeModal()
        return
      }

      this.playVideo(data)
    },

    playVideo(video: ViewingVideo) {
      this.videoSource = video.src
      this.videoPoster = video.poster
      this.isTeaser = video.isTeaser
      this.teaserEnded = false
      this.isVideoFullscreen = false

      this.triggerVideoModal()
    },

    onVideoEnded() {
      if (!this.isTeaser) {
        return
      }

      this.teaserEnded = true

      if (this.isVideoFullscreen) {
        this.player().exitFullscreen()
      }

      nextTick(() => {
        const cta = this.$refs.teaserCta as HTMLElement | undefined
        cta?.focus()
      })
    },

    replayTeaser() {
      const player = this.player()

      this.teaserEnded = false
      player.currentTime = 0
      player.play()
    },

    player(): MediaPlayerElement {
      return this.$refs.videoPlayer as MediaPlayerElement
    },

    triggerVideoModal() {
      nextTick(() => {
        this.showModal()
      })
    },
  },
  setup() {
    const { showModal } = useModal('property-video')

    return {
      showModal,
      showUpgradeModal,
    }
  },
})
